import type { FieldValidationError } from "@/shared/types/workflow/workflow-form.types";
import { isCommentFieldValue } from "@/shared/utils/field-value-type-guards";
import { isRichTextValue } from "@/shared/utils/rich-text";
import type { Field } from "@spz/shared-domain";
import type { TFunction } from "i18next";
import { validateComment } from "../comment";
import { validateFileUpload } from "../file-upload";
import { validateTextArea } from "../textarea";
import { validateRequired } from "./validate-required";

/**
 * Validates a field according to its type and constraints
 */
export function validateFieldValue({
  field,
  value,
  t,
}: {
  field: Field;
  value: unknown;
  t: TFunction;
}): FieldValidationError | undefined {
  const INVALID_VALUE_ERROR: FieldValidationError = {
    translationKey: "workflow_validation_invalid_value",
    type: "validate",
    message: t("workflow_validation_invalid_value"),
  };
  // First, validate required
  const requiredError = validateRequired({ field, value, t });
  if (requiredError) {
    return requiredError;
  }

  // Skip further validation if field is empty and not required
  if (!value) {
    return undefined;
  }

  // Validate field-specific rules based on type
  switch (field.type) {
    case "textarea": {
      if (!isRichTextValue(value)) {
        return INVALID_VALUE_ERROR;
      }
      return validateTextArea({ field, value, t });
    }

    case "comment": {
      if (!isCommentFieldValue(value)) {
        return INVALID_VALUE_ERROR;
      }
      return validateComment({ field, value, t });
    }

    case "file_upload": {
      if (!(value instanceof File)) {
        return INVALID_VALUE_ERROR;
      }
      return validateFileUpload({ field, value, t });
    }

    case "select":
    case "conclusion_select":
      // Select fields only need required validation, which is already handled
      return undefined;

    default:
      return undefined;
  }
}
